import { memo, useContext } from 'react'
import styled from 'styled-components'
import { Box, Pill, Typography } from '@astro-ui/components'
import store from 'storeContext'
import { formatMoneyRMG } from 'helpers'
import { styleBusinessTagging } from 'features/Product/constant/product'

const widthAssetList = {
  w2: '20',
  w3: '30',
  w4: '15',
  w5: '15',
  w6: '15',
  w7: '20',
}

// Jumlah PO has few digits, so it stays fixed. The price columns (Harga Satuan,
// Harga Diskon, Total) are sized first from their own longest formatted value and
// always get the room they need. Whatever's left is split between Nomor SKU and
// Nama Produk, with Nomor SKU shrinking first since it's a single unbroken token
// that just wraps to a second line, while Nama Produk keeps its readability floor.
const FIXED_W4 = 10
const FLEX_TOTAL = 100 - FIXED_W4

const PRICE_CHAR_WIDTH = 1.5 // % width per formatted character
const PRICE_BASE_PADDING = 4
const MIN_PRICE_COL = 9
const MAX_PRICE_COL = 30

const SKU_CHAR_WIDTH = 0.9
const SKU_BASE_PADDING = 3
const MIN_SKU_COL = 8
const MAX_SKU_COL = 14

const MIN_NAME_COL = 14
const MIN_NON_PRICE_TOTAL = MIN_SKU_COL + MIN_NAME_COL

function getMaxFormattedLength(items, getValue) {
  return (items || []).reduce((max, item) => Math.max(max, getValue(item)?.length || 0), 0)
}

function widthFromLength(length, charWidth, basePadding, min, max) {
  return Math.min(Math.max(length * charWidth + basePadding, min), max)
}

function getDynamicWidths(items) {
  const lenW2 = getMaxFormattedLength(items, (item) => String(item?.product_sku ?? ''))
  const lenW5 = getMaxFormattedLength(items, (item) => formatMoneyRMG(item?.buy_price || 0, ''))
  const lenW6 = getMaxFormattedLength(items, (item) =>
    formatMoneyRMG(item?.discounted_price || 0, ''),
  )
  const lenW7 = getMaxFormattedLength(items, (item) => formatMoneyRMG(Number(item?.total) || 0, ''))

  let w5 = widthFromLength(
    lenW5,
    PRICE_CHAR_WIDTH,
    PRICE_BASE_PADDING,
    MIN_PRICE_COL,
    MAX_PRICE_COL,
  )
  let w6 = widthFromLength(
    lenW6,
    PRICE_CHAR_WIDTH,
    PRICE_BASE_PADDING,
    MIN_PRICE_COL,
    MAX_PRICE_COL,
  )
  let w7 = widthFromLength(
    lenW7,
    PRICE_CHAR_WIDTH,
    PRICE_BASE_PADDING,
    MIN_PRICE_COL,
    MAX_PRICE_COL,
  )

  const priceTotal = w5 + w6 + w7
  let nonPriceTotal = FLEX_TOTAL - priceTotal

  if (nonPriceTotal < MIN_NON_PRICE_TOTAL) {
    const scale = (FLEX_TOTAL - MIN_NON_PRICE_TOTAL) / priceTotal
    w5 *= scale
    w6 *= scale
    w7 *= scale
    nonPriceTotal = MIN_NON_PRICE_TOTAL
  }

  const skuContentWidth = widthFromLength(
    lenW2,
    SKU_CHAR_WIDTH,
    SKU_BASE_PADDING,
    MIN_SKU_COL,
    MAX_SKU_COL,
  )
  const w2 = Math.min(skuContentWidth, Math.max(nonPriceTotal - MIN_NAME_COL, MIN_SKU_COL))
  const w3 = nonPriceTotal - w2

  return { w2, w3, w4: FIXED_W4, w5, w6, w7 }
}

function TableMain() {
  const {
    state: {
      dataPOLiteCreatePO: { items },
      isAsset,
    },
  } = useContext(store.PrintAreaContext)

  const widthList = isAsset ? widthAssetList : getDynamicWidths(items)

  return (
    <Container>
      <thead>
        <Tr style={{ backgroundColor: '#E9E9E9' }}>
          <Th wd={widthList.w2}>Nomor SKU</Th>
          <Th wd={widthList.w3}>Nama Produk</Th>
          <Th wd={widthList.w4}>Jumlah PO</Th>
          <Th wd={widthList.w5}>Harga Satuan</Th>
          <Th wd={widthList.w6}>Harga Diskon</Th>
          <Th wd={widthList.w7}>Total</Th>
        </Tr>
      </thead>
      <tbody>
        {items?.map((el, i) => (
          <Row
            data={el}
            key={'el.supply_order_item_id' + i}
            idx={i}
            widthList={widthList}
            isAsset={isAsset}
          />
        ))}
      </tbody>
    </Container>
  )
}

function Row({ data, idx, widthList, isAsset }) {
  const wrapOverflow = !isAsset

  return (
    <TrRow>
      <Td wd={widthList.w2} wrapOverflow={wrapOverflow}>
        <Typography variant="body2">{data?.product_sku}</Typography>
      </Td>
      <Td wd={widthList.w3} wrapOverflow={wrapOverflow}>
        <Typography variant="body2">{data?.product_name}</Typography>
        <Box display="flex" flexWrap="wrap">
          {data.business_taggings.length > 0 &&
            data.business_taggings.map((taggingItem) => (
              <Pill
                key={taggingItem.key}
                content={taggingItem.value}
                sx={{
                  marginTop: '5px',
                  marginRight: '3px',
                  ...styleBusinessTagging[taggingItem.key],
                }}
              />
            ))}
        </Box>
      </Td>

      <Td wd={widthList.w4} wrapOverflow={wrapOverflow}>
        <Typography variant="body2">{data?.incoming_quantity}</Typography>
      </Td>
      <Td wd={widthList.w5} wrapOverflow={wrapOverflow}>
        <Typography variant="body2">
          {formatMoneyRMG(data?.buy_price ? data?.buy_price : 0, '') || '-'}
        </Typography>
      </Td>
      <Td wd={widthList.w6} wrapOverflow={wrapOverflow}>
        <Typography variant="body2">{formatMoneyRMG(data?.discounted_price, '') || '-'}</Typography>
      </Td>
      <Td wd={widthList.w7} wrapOverflow={wrapOverflow}>
        <Typography variant="body2">{formatMoneyRMG(Number(data.total), '')}</Typography>
      </Td>
    </TrRow>
  )
}
export default memo(TableMain)

const TrRow = styled.tr`
  background-color: ${({ theme: { colors } }) => colors.white};
  align-items: center;
  display: flex;
  page-break-inside: avoid;
  break-inside: avoid;
`
const Td = styled.td`
  color: ${({ theme: { colors } }) => colors.black};
  font-size: ${({ theme: { fontSizes } }) => fontSizes.smaller};
  padding: 12px;
  text-align: start;
  ${({ wrapOverflow }) => wrapOverflow && 'overflow-wrap: anywhere;'}
  width: ${({ wd }) => {
    return `${wd}%`
  }};
`

const Container = styled.table`
  width: 100%;
  margin-top: 20px;
  border: 1px solid #d3d3d3;
  border-radius: 8px;
`

const Tr = styled.tr`
  background-color: ${({ theme: { colors } }) => colors.tableHead};
  display: flex;
`
const Th = styled.th`
  color: #212121;
  font-size: ${({ theme: { fontSizes } }) => fontSizes.smaller};
  text-align: start;
  padding: 16px 12px;
  width: ${({ wd }) => {
    return `${wd}%`
  }};
`
